import type {
    ConstrainedSearchResponse,
    MaterializedAction,
} from './types';

let REST_BASE = '';
let NONCE = '';

export function configureApi(restBase: string, nonce: string): void {
    REST_BASE = restBase.replace(/\/$/, '');
    NONCE = nonce;
}

function restRoot(): string {
    // restBase is typically .../wp-json/gas/v1
    return REST_BASE;
}

async function postJson<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const res = await fetch(`${restRoot()}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': NONCE,
        },
        credentials: 'same-origin',
        body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
        const msg =
            (json as { message?: string })?.message
            || (json as { data?: { message?: string } })?.data?.message
            || `HTTP ${res.status}`;
        throw new Error(msg);
    }
    // Enveloppe GAS : { data, meta }
    const data = (json as { data?: T })?.data ?? json;
    return data as T;
}

export interface ConstrainedSearchRequest {
    query: string;
    locale?: string;
    timezone?: string;
    as_of?: string;
    access_level?: string;
}

export async function constrainedSearch(
    req: ConstrainedSearchRequest,
): Promise<ConstrainedSearchResponse> {
    return postJson<ConstrainedSearchResponse>('/location/search/constrained', {
        query: req.query,
        locale: req.locale ?? 'fr',
        timezone: req.timezone ?? '',
        as_of: req.as_of ?? '',
        access_level: req.access_level ?? 'public',
    });
}

export async function materializeAction(
    type: string,
    context: Record<string, unknown>,
): Promise<MaterializedAction> {
    const data = await postJson<{ action: MaterializedAction; mutates?: boolean }>(
        '/location/search/constrained/action',
        { type, ...context },
    );
    if (!data?.action) {
        throw new Error('action_missing');
    }
    // Filet client : la recherche ne mute jamais.
    if (data.action.mutates === true || data.mutates === true) {
        throw new Error('unexpected_mutation');
    }
    if (data.action.payload) {
        data.action.payload.executes = false;
    }
    return data.action;
}

/** Télémétrie — fire-and-forget, jamais bloquant pour l'UX. */
export async function telemetryRun(payload: Record<string, unknown>): Promise<{
    accepted?: boolean;
    query_id?: string;
    session_token?: string | null;
} | null> {
    try {
        return await postJson('/search/telemetry/run', payload);
    } catch {
        return null;
    }
}

export async function telemetryEvent(payload: Record<string, unknown>): Promise<void> {
    try {
        await postJson('/search/telemetry/event', payload);
    } catch {
        /* ignore */
    }
}

let sessionToken: string | null = null;

export function getTelemetrySession(): string | null {
    return sessionToken;
}

export function setTelemetrySession(token: string | null | undefined): void {
    if (token) sessionToken = token;
}
